import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import React, {useEffect, useMemo, useState, useTransition} from "react";
import {staticReportStore} from "../../../../../stores/stores";
import {
  getSearchHistoryUniversity,
  getSearchKeywordUniversity,
  getUnitActionUniversity
} from "../../../../../service/statistical-report";
import {BarStackedChartProps, LineChartProps, PieChart} from "../../../../../types/static-report";
import {debounce} from "lodash";
import UserBehaviorChartTab from "../chart/user-behavior-tab/UserBehaviorChartTab";
import BehaviorTable from "../table/behavior-table";

type Props = {
  dataType: string;
  universityId?: number;
};

const UserBehaviorTab = (props: Props) => {
  const { t } = useTranslation();
  const {dataType, universityId} = props;
  const {filterValues, tab} = staticReportStore()
  const [isPending, startTransition] = useTransition()
  const [actionModuleMapping, setActionModuleMapping] = useState({
    courseStructureType: null,
    actionType: null,
    moduleGroup: null
  });

  const [chartData, setChartData] = useState({
    searchKeywordUniversity: [] as PieChart[],
    searchHistoryUniversity: [] as BarStackedChartProps[],
    unitDownloadResource: [] as LineChartProps[],
    unitPreviewResource: [] as LineChartProps[],
    unitShareResource: [] as LineChartProps[],
  });

  const [loading, setLoading] = useState({
    searchKeywordUniversity: false,
    searchHistoryUniversity: false,
    unitDownloadResource: false,
    unitPreviewResource: false,
    unitShareResource: false,
  });

  const payload = useMemo(
    () => ({
      from: dayjs(filterValues.dateFrom).toISOString(),
      to: dayjs(filterValues.dateTo).toISOString(),
      courseLevelIds: filterValues?.courseLevelIds,
      industryGroupIds: filterValues?.industryGroupIds,
      courseIds: filterValues?.courseIds,
      universityIds: filterValues?.universityIds,
      timeUnit: filterValues.timeUnit,
      teacherIds: filterValues?.lecturerIds
    }),
    [
      filterValues.dateFrom,
      filterValues.dateTo,
      filterValues?.courseLevelIds,
      filterValues?.industryGroupIds,
      filterValues?.courseIds,
      filterValues?.lecturerIds,
      filterValues.timeUnit,
      filterValues?.lecturerIds
    ]
  );

  const fetchData = async () => {
    try {
      const apiCallList = [
        {
          key: "searchHistoryUniversity",
          api: getSearchHistoryUniversity,
          params: payload,
        },
        {
          key: "searchKeywordUniversity",
          api: getSearchKeywordUniversity,
          params: payload,
        },
        {
          key: "unitDownloadResource",
          api: getUnitActionUniversity,
          params: {
            ...payload,
            courseStructureType: "unit",
            moduleGroup: 3,
            unitActionType: 3
          },
        },
        {
          key: 'unitPreviewResource',
          api: getUnitActionUniversity,
          params: {
            ...payload,
            courseStructureType: "unit",
            moduleGroup: 3,
            unitActionType: 1
          },
        },
        {
          key: 'unitShareResource',
          api: getUnitActionUniversity,
          params: {
            ...payload,
            courseStructureType: "unit",
            moduleGroup: 3,
            unitActionType: 2
          },
        }
      ]
      await Promise.allSettled(
        apiCallList.map(async ({key, api, params}) => {
          setLoading((prev) => ({...prev, [key]: true}));
          try {
            const response = await api(params);
            startTransition(() => {
              setChartData((prevState) => ({
                ...prevState,
                [key]: response?.data?.data?.statistic || response?.data?.data,
              }));
            })
          } catch (error) {
            console.error(`Error fetching data for ${key}:`, error);
            startTransition(() => {
              setChartData(prevState => ({
                ...prevState,
                [key]: [], // Provide fallback empty data
              }));
            });
          } finally {
            setLoading((prev) => ({...prev, [key]: false}));
          }
        })
      );
    } catch (err) {
      console.error(err)
    }
  }

  const fetchUnitActionForUniversity = async (dataActionModuleMapping: any) => {
    switch (dataActionModuleMapping?.actionType) {
      case 1:
        setLoading((prev) => ({...prev, unitPreviewResource: true}));
        break;
      case 2:
        setLoading((prev) => ({...prev, unitShareResource: true}));
        break;
      case 3:
        setLoading((prev) => ({...prev, unitDownloadResource: true}));
        break;
      default:
        break;
    }

    try {
      const data = await getUnitActionUniversity({
        ...payload,
        courseStructureType: dataActionModuleMapping?.courseStructureType,
        moduleGroup: dataActionModuleMapping?.moduleGroup,
        unitActionType: dataActionModuleMapping?.actionType,
      });
      switch (dataActionModuleMapping?.actionType) {
        case 1:
          setChartData((prevState) => ({
            ...prevState,
            unitPreviewResource: data?.data?.data,
          }));
          break;
        case 2:
          setChartData((prevState) => ({
            ...prevState,
            unitShareResource: data?.data?.data,
          }));
          break;
        case 3:
          setChartData((prevState) => ({
            ...prevState,
            unitDownloadResource: data?.data?.data,
          }));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error fetching unitReviewAndAccessForMinistry data:", error);
    } finally {
      switch (dataActionModuleMapping?.actionType) {
        case 1:
          setLoading((prev) => ({...prev, unitPreviewResource: false}));
          break;
        case 2:
          setLoading((prev) => ({...prev, unitShareResource: false}));
          break;
        case 3:
          setLoading((prev) => ({...prev, unitDownloadResource: false}));
          break;
        default:
          break;
      }
    }
  };
  const debouncedFetchData = useMemo(
    () => debounce(fetchData, 300),
    [filterValues]
  );

  const debouncedFetchUnitActionForUniversity = useMemo(
    () => debounce(fetchUnitActionForUniversity, 300),
    [filterValues]
  );

  useEffect(() => {
    if (tab === "4" && universityId && universityId !== 0) {
      debouncedFetchData();
    }
    return () => {
      debouncedFetchData.cancel();
    };
  }, [filterValues, tab, universityId]);

  useEffect(() => {
    if (tab === "4" && actionModuleMapping.actionType && actionModuleMapping.moduleGroup
      && universityId && universityId !== 0) {
      debouncedFetchUnitActionForUniversity(actionModuleMapping);
    }
    return () => {
      debouncedFetchUnitActionForUniversity.cancel();
    };
  }, [actionModuleMapping, universityId]);

  const memoizedChartData = useMemo(() => {
    return {
      searchHistoryUniversity: chartData.searchHistoryUniversity,
      searchKeywordUniversity: chartData.searchKeywordUniversity,
      unitDownloadResource: chartData.unitDownloadResource,
      unitPreviewResource: chartData.unitPreviewResource,
      unitShareResource: chartData.unitShareResource,
    };
  }, [chartData]);

  return (
    (universityId && universityId !== 0)
      ?
      (dataType === "1") ?
        <UserBehaviorChartTab {...memoizedChartData} loading={loading}
                              setActionModuleMapping={setActionModuleMapping}/> :
        <BehaviorTable {...memoizedChartData} loading={isPending} actionModuleMapping={actionModuleMapping}
                       setActionModuleMapping={setActionModuleMapping}/>
      :
      <p style={{fontSize: 18, color: "#5f5c5c", textAlign: "center", marginTop: "30vh"}}>{t("Không có dữ liệu", "Không có dữ liệu")}</p>
  )
};

export default UserBehaviorTab;
